import { Box, Field, Input } from '@chakra-ui/react';
import type React from 'react';

interface TextInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  isRequired?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  isReadOnly?: boolean;
  maxLength?: number;
  type?: 'text' | 'email' | 'url' | 'password';
}

export const TextInput: React.FC<TextInputProps> = ({
  label,
  value,
  onChange,
  isRequired = false,
  placeholder,
  size = 'md',
  isReadOnly = false,
  maxLength,
  type = 'text',
  ...props
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Apply maxLength constraint if specified
    if (maxLength && inputValue.length > maxLength) {
      return;
    }

    onChange(inputValue);
  };

  const isInvalidValue = () => {
    if (value === '' && !isRequired) return false;
    if (isRequired && value.trim() === '') return true;
    return false;
  };

  const getErrorMessage = () => {
    if (isRequired && value.trim() === '') return 'This field is required';
    return 'Please enter a valid value';
  };

  // If no label is provided, return just the input without Field wrapper
  if (!label) {
    return (
      <Input
        type={type}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        size={size}
        readOnly={isReadOnly}
        maxLength={maxLength}
        {...props}
      />
    );
  }

  return (
    <Field.Root required={isRequired}>
      <Field.Label>
        {label}
        {isRequired && <Field.RequiredIndicator />}
      </Field.Label>
      <Box width="100%">
        <Input
          type={type}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          size={size}
          readOnly={isReadOnly}
          maxLength={maxLength}
          width="100%"
          {...props}
        />
      </Box>
      {isInvalidValue() && <Field.HelperText color="red.500">{getErrorMessage()}</Field.HelperText>}
    </Field.Root>
  );
};
